import { Autocomplete, AutocompleteRenderInputParams, Stack, TextField, Typography } from "@mui/material";
import React, { forwardRef, useCallback, useMemo } from "react";

export interface IStringOption<T extends string = string> {
    value: T;
    label?: string;
    sublabel?: string;
    description?: string;
}

type IClearableProps<T extends string> =
    | { required: true; onChange?(option: T): void }
    | { required?: false; onChange?(option: T | undefined): void };

export type IStringOptionSelectProps<T extends string = string> = IClearableProps<T> & {
    error?: boolean;
    label?: string;
    placeholder?: string;
    helperText?: string;

    options: Array<IStringOption<T>>;
    value: T | undefined;

    onBlur?(): void;
};

function StringOptionSelectInternal<T extends string>(
    { required, error, label, placeholder, helperText, options, value, onBlur, onChange }: IStringOptionSelectProps<T>,
    ref: React.ForwardedRef<HTMLInputElement>,
): React.ReactElement | null {
    const selectedOption = useMemo(() => options.find((option) => option.value === value), [options, value]);

    const renderStringInput = useCallback(
        (params: AutocompleteRenderInputParams): React.ReactNode => (
            <TextField
                {...params}
                inputRef={ref}
                error={error}
                label={label}
                placeholder={placeholder}
                helperText={helperText}
                inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                }}
            />
        ),
        [ref, label, placeholder, helperText, error],
    );

    const handleChange = useCallback(
        (_: unknown, option: IStringOption<T> | null): void => {
            if (option == null) {
                if (required) {
                    return;
                }
                onChange?.(undefined);
            } else {
                onChange?.(option.value);
            }
        },
        [required, onChange],
    );

    return (
        <Autocomplete<IStringOption<T>, false, boolean, false>
            size="small"
            autoHighlight={true}
            disableClearable={required}
            options={options}
            value={selectedOption ?? null}
            getOptionLabel={getStringOptionLabel}
            renderInput={renderStringInput}
            renderOption={renderStringOption}
            onBlur={onBlur}
            onChange={handleChange}
        />
    );
}
export const StringOptionSelect = forwardRef(StringOptionSelectInternal) as <T extends string>(
    props: IStringOptionSelectProps<T> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof StringOptionSelectInternal>;

const getStringOptionLabel = (option: IStringOption): string => {
    if (option.sublabel) {
        return `${option.label ?? option.value} ${option.sublabel}`;
    }
    return option.label ?? option.value;
};

const renderStringOption = (props: React.HTMLAttributes<HTMLLIElement>, option: IStringOption): React.ReactNode => (
    <li {...props}>
        <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
                <Typography>{option.label ?? option.value}</Typography>
                {option.sublabel && (
                    <Typography color="text.secondary" fontWeight="light">
                        {option.sublabel}
                    </Typography>
                )}
            </Stack>
            {option.description && (
                <Typography variant="label1" fontWeight="regular">
                    {option.description}
                </Typography>
            )}
        </Stack>
    </li>
);
